import React, { useState, useEffect, useRef } from "react";
import {
  CreationTable,
  CreationTableHandle,
} from "./RiskAssementCreator/CreationTable";
import Modal from "../../components/modal/Modal";
import { DotLoader } from "../../components/loader/Loader";
import { OrgUserType } from "../../types/api";
import { whrDataFormate, ahrDataFormate } from "../../data/RiskAssesment";
import RiskAssesmentForm from "./RiskAssementCreator/RiskAssesmentForm";
import SignatureForm from "./RiskAssementCreator/SignatureForm";
import { useOrgUsersStore, useRiskAssessment } from "../../store/store";
import { CheckIcon } from "lucide-react";
import { useS3Upload } from "../../utils/s3Client";

interface IProps {
  completionCloseModal: () => void;
  assessmentId?: string;
  mode?: "create" | "edit" | "view";
  moduleName?: string;
}

const RiskAssementCreation: React.FC<IProps> = ({
  completionCloseModal,
  assessmentId,
  mode = "create",
  moduleName,
}) => {
  const isReadOnly = mode === "view";
  const [isSubmitSuccess, setIsSubmitSuccess] = useState<boolean>(false);
  const {
    riskAssessment,
    hwrList,
    asrList,
    loadingRisks,
    clearMessages,
    AssetsTask,
  } = useRiskAssessment();
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();
  const riskFormRef = useRef<{ createRiskForm: () => void }>(null);
  const submitFormRef = useRef<{ submitRiskForm: () => void }>(null);
  const hwrTableRef = useRef<CreationTableHandle>(null);
  const asrTableRef = useRef<CreationTableHandle>(null);
  const { uploadFileToS3, progress, uploading, error } = useS3Upload();
  const [errMessage, setErrMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resMessage, setResMessage] = useState<string>("");
  const [selectedPremise, setSelectedPremise] = useState<string[] | null>(null);
  const [premiseUsers, setPremiseUsers] = useState<OrgUserType[]>([]);
  const [formlevel, setFormLevel] = useState<number>(1);

  const formMove = (type: string) => {
    if (isReadOnly && type === "next" && formlevel === 1) {
      setErrMessage("");
      setFormLevel(2);
      return;
    }
    if (formlevel === 1 && type === "next") {
      if (riskFormRef.current) {
        riskFormRef.current.createRiskForm();
      }
      return;
    }
    if (type === "next" && formlevel === 2 && !isReadOnly) {
      if (
        hwrTableRef.current &&
        !hwrTableRef.current.checkBeforeNext()
      ) {
        return;
      }
    }
    if (type === "next" && formlevel === 3 && !isReadOnly) {
      if (
        asrTableRef.current &&
        !asrTableRef.current.checkBeforeNext()
      ) {
        return;
      }
    }
    const moveto =
      type === "next"
        ? formlevel + 1
        : formlevel - 1;

    setErrMessage("");
    setFormLevel(moveto);
  };

  const submitRisk = async () => {
    if (isReadOnly) {
      return;
    }
    if (submitFormRef.current) {
      await submitFormRef.current.submitRiskForm();
    }
    AssetsTask(riskAssessment._id ?? "");
  };

  useEffect(() => {
    if (!orgUsersData?.length) fetchOrgUsers();
    if (!selectedPremise || !orgUsersData?.length) return;

    const selectedSet = new Set(selectedPremise);
    const filteredUsers = orgUsersData.filter((user: OrgUserType) => {
      if (!user.premise) return false;

      let premiseIds: string[] = [];
      if (typeof user.premise === "string") {
        premiseIds = [user.premise];
      } else if (Array.isArray(user.premise)) {
        premiseIds = user.premise.map((p) =>
          typeof p === "string" ? p : p?.$oid,
        );
      }

      return premiseIds.some((id) => selectedSet.has(id));
    });
    setPremiseUsers(filteredUsers);
  }, [selectedPremise, orgUsersData]);
  return (
    <>
      <div
        className="bg-[#eef1f4] w-[100vw] h-[100vh] flex flex-col overflow-hidden"
        style={{
          zIndex: 9999,
          borderRadius: "0px",
        }}
      >
        <header className="px-6 pt-4 pb-2 bg-white border-b border-[#e3e8ec]">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl font-bold text-[#16202b]">
              {mode === "view"
                ? "View Risk Assessment"
                : mode === "edit"
                  ? "Edit Risk Assessment"
                  : "Create Risk Assessment"}
            </h2>
            <button
              onClick={() => {
                if (window.confirm("Discard this risk assessment?")) {
                  completionCloseModal();
                }
              }}
              className="text-[#43525f] hover:text-[#16202b] text-2xl font-bold leading-none px-2 py-1 hover:bg-[#eef2f5] rounded transition-colors"
              title="Close"
            >
              ×
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-2 w-full bg-[#d7dee4] rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-[#0e9f8e] rounded-full transition-all duration-300"
              style={{ width: `${(formlevel / 4) * 100}%` }}
            />
          </div>

          {/* Step labels */}
          <div className="flex justify-between text-[11px] font-semibold text-[#7b8a97]">
            <span className={`flex-1 text-center transition-colors ${formlevel > 1 ? "text-[#0e9f8e]" : formlevel === 1 ? "text-[#16202b]" : ""}`}>
              Details
            </span>
            <span className={`flex-1 text-center transition-colors ${formlevel > 2 ? "text-[#0e9f8e]" : formlevel === 2 ? "text-[#16202b]" : ""}`}>
              Hospital wide risks
            </span>
            <span className={`flex-1 text-center transition-colors ${formlevel > 3 ? "text-[#0e9f8e]" : formlevel === 3 ? "text-[#16202b]" : ""}`}>
              Area wide risks
            </span>
            <span className={`flex-1 text-center transition-colors ${formlevel > 4 ? "text-[#0e9f8e]" : formlevel === 4 ? "text-[#16202b]" : ""}`}>
              Overall report
            </span>
          </div>
        </header>

        {errMessage !== "" && (
          <p className="text-sm px-6 my-2 text-red-700 font-semibold">{errMessage}</p>
        )}

        <div className="flex-1 min-h-0 bg-white overflow-auto">
          {formlevel === 1 && (
            <RiskAssesmentForm
              sendErrorMessage={(msg: string) => setErrMessage(msg)}
              sendLoader={(loader: boolean) => setIsLoading(loader)}
              ref={riskFormRef}
              assessmentId={assessmentId}
              movenext={(val: number) => {
                const newLevel = formlevel + val;
                setFormLevel(newLevel);
              }}
              isReadOnly={isReadOnly}
              sendSelectedPremise={(premiseIds: string[]) =>
                setSelectedPremise(premiseIds)
              }
              premiseUsers={premiseUsers}
              moduleName={moduleName}
            />
          )}
          {formlevel === 2 && (
            <CreationTable
              ref={hwrTableRef}
              initialData={hwrList}
              structure={whrDataFormate(premiseUsers, moduleName)}
              type={"HWR"}
              isReadOnly={isReadOnly}
              moduleName={moduleName}
            />
          )}
          {formlevel === 3 && (
            <CreationTable
              ref={asrTableRef}
              initialData={asrList}
              structure={ahrDataFormate(premiseUsers, moduleName)}
              type={"AWR"}
              isReadOnly={isReadOnly}
              moduleName={moduleName}
            />
          )}
          {formlevel === 4 && (
            <SignatureForm
              sendErrorMessage={(msg: string) => setErrMessage(msg)}
              sendLoader={(loader: boolean) => setIsLoading(loader)}
              sendSuccess={(status: boolean) => setIsSubmitSuccess(status)}
              ref={submitFormRef}
              isReadOnly={isReadOnly}
              moduleName={moduleName}
            />
          )}
        </div>

        <footer className="bg-[#5c6b7c] px-6 py-3 flex justify-end gap-3 mt-auto">
          {/* PREVIOUS */}
          {formlevel > 1 && (
            <button
              type="button"
              className="px-6 py-2 bg-[#8996a5] hover:bg-[#7c8998] text-white font-semibold rounded-full text-sm transition-colors"
              onClick={() => formMove("prev")}
            >
              PREVIOUS
            </button>
          )}

          {/* NEXT */}
          {formlevel < 4 && (
            <button
              type="button"
              className="px-6 py-2 bg-[#0e9f8e] hover:bg-[#0b8577] text-white font-semibold rounded-full text-sm transition-colors"
              onClick={() => {
                formMove("next");
              }}
            >
              NEXT
            </button>
          )}

          {/* LAST STEP BUTTONS */}
          {formlevel === 4 && (
            <>
              {/* VIEW MODE → CLOSE */}
              {mode === "view" && (
                <button
                  type="button"
                  className="px-6 py-2 bg-[#0e9f8e] hover:bg-[#0b8577] text-white font-semibold rounded-full text-sm transition-colors"
                  onClick={() => {
                    completionCloseModal();
                  }}
                >
                  CLOSE
                </button>
              )}

              {/* DRAFT → SUBMIT */}
              {mode !== "view" && riskAssessment.status === "open" && (
                <button
                  type="button"
                  className="px-6 py-2 bg-[#0e9f8e] hover:bg-[#0b8577] text-white font-semibold rounded-full text-sm transition-colors"
                  onClick={() => {
                    submitRisk();
                  }}
                >
                  SUBMIT
                </button>
              )}

              {/* SUBMITTED + EDIT MODE → UPDATE */}
              {mode === "edit" && riskAssessment.status !== "open" && (
                <button
                  type="button"
                  className="px-6 py-2 bg-[#0e9f8e] hover:bg-[#0b8577] text-white font-semibold rounded-full text-sm transition-colors"
                  onClick={() => {
                    submitRisk();
                  }}
                >
                  UPDATE
                </button>
              )}
            </>
          )}
        </footer>
      </div>

      {/* ------------------- All loders and modals ----------------- */}
      {/* progress for S3 Bucket */}
      {progress > 0 && progress < 100 && (
        <div className="absmiddle" style={{ width: "100px", height: "50px" }}>
          {/* <p>Upload Progress: {progress}%</p> */}
          <div className="relative size-40" style={{ transform: "scale(0.7)" }}>
            <svg
              className="size-full -rotate-90"
              viewBox="0 0 36 36"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                strokeDasharray: 100,
                strokeDashoffset: 100 - progress,
              }}
            >
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                className="stroke-current text-gray-200 dark:text-neutral-700"
                stroke-width="2"
              ></circle>
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                className="stroke-current text-blue-600 dark:text-blue-500"
                stroke-width="2"
                stroke-linecap="round"
              ></circle>
            </svg>

            <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <span className="text-center text-2xl font-bold text-blue-600 dark:text-blue-500">
                {progress}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Common loader */}
      {(isLoading || loadingRisks) && (
        <div
          className="absmiddle bg-[#ffffff40]"
          style={{ width: "100vw", height: "100vh", zIndex: 9999 }}
        >
          <div className="absmiddle" style={{ width: "100px", height: "50px" }}>
            <DotLoader />
          </div>
        </div>
      )}
      {/* APi res message alert */}
      {/* <Modal isOpen={resMessage !== ""} onClose={() => closeModal()}>
        <div className="bg-white w-[500px]  rounded-xl  overflow-hidden ">
          <div className="p-6 leading-none">
            <div className="flex flex-col gap-4 mt-4">
              <label htmlFor="folderName" className="text-red-500">
                {resMessage}
              </label>
            </div>
          </div>
          <div className="flex  bg-[#61748E] justify-end py-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full "
              onClick={() => closeModal()}
            >
              Close
            </button>
          </div>
        </div>
      </Modal> */}

      {/* Successfully Risk Assesment Creation */}
      <Modal
        isOpen={isSubmitSuccess}
        onClose={() => {
          setIsSubmitSuccess(false);
          completionCloseModal();
        }}
      >
        <div className="bg-white w-[500px]  rounded-xl  overflow-hidden ">
          <div className="p-6 leading-none">
            <div className="block text-center">
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  margin: "auto",
                  borderRadius: "100%",
                  border: "1px solid #0fa9a6",
                }}
                className="bg-green-100"
              >
                <CheckIcon
                  style={{ margin: "18px auto" }}
                  className="text-[#0fa9a6]"
                />
              </div>

              <h4
                className="mt-2 text-gray-800"
                style={{ fontSize: "18px", lineHeight: "20px" }}
              >
                Successfully Updated <br /> Risk Assessment &amp; MItigation
                Plan Details.
              </h4>
            </div>
          </div>
          <div className="flex pb-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full m-auto text-white uppercase"
              onClick={() => {
                setIsSubmitSuccess(false);
                completionCloseModal();
              }}
            >
              {" "}
              Continue{" "}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RiskAssementCreation;

/**
 * update success message = done
 * add HWR and AWR
 *
 */
